import type { CanvasFormat, ChainEntry, GameChain, ReplayStroke } from './types';

const GIF_WIDTH = 420;
const GIF_HEIGHT = 560;
const BODY_TOP = 78;
const BODY_BOTTOM = 548;
const MAX_EXTRA_DRAW_FRAMES = 140;

type ProgressCallback = (percent: number, label: string) => void;

type RenderStep = {
  chainIndex: number;
  entryIndex: number;
  drawingProgress: number;
  delayMs: number;
};

export interface AlbumGifOptions {
  animateDrawings?: boolean;
}

export async function createAlbumGifBlob(
  chains: GameChain[],
  canvasFormat: CanvasFormat,
  onProgress?: ProgressCallback,
  options: AlbumGifOptions = {}
): Promise<Blob> {
  if (!chains.length) throw new Error('لا توجد بيانات ألبوم متاحة للتحميل.');

  onProgress?.(1, 'تجهيز الألبوم...');
  await document.fonts?.ready?.catch(() => undefined);

  const canvas = document.createElement('canvas');
  canvas.width = GIF_WIDTH;
  canvas.height = GIF_HEIGHT;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('المتصفح لا يدعم إنشاء ملف GIF.');

  const steps = buildRenderSteps(chains, options.animateDrawings === true);
  const encoder = new GifEncoder(GIF_WIDTH, GIF_HEIGHT, 0);
  const imageCache = new Map<string, HTMLImageElement>();

  for (let index = 0; index < steps.length; index++) {
    const step = steps[index];
    await renderAlbumFrame(ctx, chains, step, canvasFormat, imageCache);
    const rgba = ctx.getImageData(0, 0, GIF_WIDTH, GIF_HEIGHT).data;
    const indexed = rgbaToRgb332(rgba);
    encoder.addFrame(indexed, Math.max(4, Math.round(step.delayMs / 10)));

    const percent = Math.round(((index + 1) / steps.length) * 96);
    onProgress?.(Math.max(2, percent), `إنشاء GIF… ${index + 1}/${steps.length}`);

    // Yield regularly so phones do not freeze for the whole export.
    if (index % 3 === 2) await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
  }

  onProgress?.(98, 'إنهاء الملف...');
  const blob = encoder.finish();
  onProgress?.(100, 'تم تجهيز ملف GIF.');
  return blob;
}

export async function exportAlbumGif(
  chains: GameChain[],
  canvasFormat: CanvasFormat,
  onProgress?: ProgressCallback
): Promise<void> {
  // Never replay stroke-by-stroke animation in downloaded round GIFs. The game
  // presentation and every archive/export show the completed drawing directly.
  const blob = await createAlbumGifBlob(chains, canvasFormat, onProgress, { animateDrawings: false });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `SketchRelay-Album-${fileTimestamp()}.gif`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function buildRenderSteps(chains: GameChain[], animateDrawings = true): RenderStep[] {
  const totalEntries = chains.reduce((sum, chain) => sum + chain.entries.length, 0);
  const drawingEntries = chains.reduce(
    (sum, chain) => sum + chain.entries.filter((entry) => entry.kind === 'drawing' && Boolean(entry.replay?.strokes.length)).length,
    0
  );
  const extraPerDrawing = animateDrawings && drawingEntries
    ? Math.max(0, Math.min(5, Math.floor(MAX_EXTRA_DRAW_FRAMES / drawingEntries)))
    : 0;

  const steps: RenderStep[] = [];
  chains.forEach((chain, chainIndex) => {
    if (!chain.entries.length) return;

    // Full-game archive GIFs intentionally use one final frame per entry.
    // This keeps large games to one final frame per entry instead of adding redundant
    // preparation/pause frames and dramatically lowers phone memory usage.
    if (!animateDrawings) {
      chain.entries.forEach((entry, entryIndex) => {
        steps.push({
          chainIndex,
          entryIndex,
          drawingProgress: 1,
          delayMs: entry.kind === 'text' ? (totalEntries > 120 ? 650 : 1000) : (totalEntries > 120 ? 500 : 750)
        });
      });
      return;
    }

    // Interactive/downloaded per-round GIFs may still animate drawing progress.
    steps.push({ chainIndex, entryIndex: 0, drawingProgress: chain.entries[0].kind === 'drawing' ? 0 : 1, delayMs: 650 });

    chain.entries.forEach((entry, entryIndex) => {
      if (entry.kind === 'drawing' && entry.replay?.strokes.length && extraPerDrawing > 0) {
        for (let part = 1; part <= extraPerDrawing; part++) {
          steps.push({
            chainIndex,
            entryIndex,
            drawingProgress: part / (extraPerDrawing + 1),
            delayMs: totalEntries > 120 ? 110 : 150
          });
        }
      }
      steps.push({
        chainIndex,
        entryIndex,
        drawingProgress: 1,
        delayMs: entry.kind === 'text' ? (totalEntries > 120 ? 650 : 1000) : (totalEntries > 120 ? 500 : 750)
      });
    });

    const lastEntryIndex = Math.max(0, chain.entries.length - 1);
    steps.push({ chainIndex, entryIndex: lastEntryIndex, drawingProgress: 1, delayMs: 500 });
  });
  return steps;
}

async function renderAlbumFrame(
  ctx: CanvasRenderingContext2D,
  chains: GameChain[],
  step: RenderStep,
  canvasFormat: CanvasFormat,
  imageCache: Map<string, HTMLImageElement>
): Promise<void> {
  const chain = chains[step.chainIndex];
  const visible = chain.entries.slice(0, step.entryIndex + 1);

  const gradient = ctx.createLinearGradient(0, 0, GIF_WIDTH, GIF_HEIGHT);
  gradient.addColorStop(0, '#5235b8');
  gradient.addColorStop(0.48, '#3149a6');
  gradient.addColorStop(1, '#176d99');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, GIF_WIDTH, GIF_HEIGHT);

  // Header
  ctx.fillStyle = 'rgba(25, 20, 82, .34)';
  ctx.fillRect(0, 0, GIF_WIDTH, 70);
  ctx.direction = 'rtl';
  ctx.textAlign = 'right';
  ctx.fillStyle = '#ffffff';
  ctx.font = '700 20px Arial, sans-serif';
  ctx.fillText(`ألبوم ${chain.originNickname}`, GIF_WIDTH - 18, 29);
  ctx.font = '600 11px Arial, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,.78)';
  ctx.fillText(`SketchRelay · ${step.chainIndex + 1}/${chains.length} · ${step.entryIndex + 1}/${chain.entries.length}`, GIF_WIDTH - 18, 50);

  const layouts = layoutVisibleEntries(ctx, visible);
  let cursorY = BODY_BOTTOM;
  for (let i = layouts.length - 1; i >= 0; i--) {
    const item = layouts[i];
    cursorY -= item.height;
    const entryIndex = visible.length - layouts.length + i;
    const entry = visible[entryIndex];
    const current = entryIndex === step.entryIndex;
    await drawChatMessage(ctx, entry, entryIndex, cursorY, item.height, current ? step.drawingProgress : 1, canvasFormat, imageCache);
    cursorY -= 10;
  }

  // Progress bar
  const globalDone = chains.slice(0, step.chainIndex).reduce((sum, c) => sum + c.entries.length, 0) + step.entryIndex + step.drawingProgress;
  const globalTotal = Math.max(1, chains.reduce((sum, c) => sum + c.entries.length, 0));
  ctx.fillStyle = 'rgba(255,255,255,.18)';
  roundRect(ctx, 18, GIF_HEIGHT - 7, GIF_WIDTH - 36, 3, 2);
  ctx.fill();
  const progress = Math.max(0, Math.min(1, globalDone / globalTotal));
  const pg = ctx.createLinearGradient(18, 0, GIF_WIDTH - 18, 0);
  pg.addColorStop(0, '#9de8ff');
  pg.addColorStop(1, '#ff9ce7');
  ctx.fillStyle = pg;
  roundRect(ctx, 18, GIF_HEIGHT - 7, (GIF_WIDTH - 36) * progress, 3, 2);
  ctx.fill();
}

function layoutVisibleEntries(ctx: CanvasRenderingContext2D, entries: ChainEntry[]): Array<{ height: number }> {
  const result: Array<{ height: number }> = [];
  let used = 0;
  for (let index = entries.length - 1; index >= 0; index--) {
    const entry = entries[index];
    const height = entry.kind === 'drawing' ? 218 : textBubbleHeight(ctx, entry.value);
    const need = height + (result.length ? 10 : 0);
    if (result.length && used + need > BODY_BOTTOM - BODY_TOP) break;
    result.unshift({ height });
    used += need;
  }
  return result;
}

function textBubbleHeight(ctx: CanvasRenderingContext2D, text: string): number {
  ctx.font = '700 17px Arial, sans-serif';
  const lines = wrapText(ctx, text || '…', 292);
  return Math.max(86, 54 + Math.min(4, lines.length) * 23);
}

async function drawChatMessage(
  ctx: CanvasRenderingContext2D,
  entry: ChainEntry,
  index: number,
  y: number,
  height: number,
  drawingProgress: number,
  canvasFormat: CanvasFormat,
  imageCache: Map<string, HTMLImageElement>
): Promise<void> {
  const right = index % 2 === 0;
  const bubbleW = entry.kind === 'drawing' ? 342 : 320;
  const x = right ? GIF_WIDTH - 18 - bubbleW : 18;
  const avatarX = right ? x + bubbleW - 17 : x + 17;
  const nameX = right ? x + bubbleW - 44 : x + 44;

  // Avatar
  ctx.beginPath();
  ctx.arc(avatarX, y + 17, 16, 0, Math.PI * 2);
  ctx.fillStyle = avatarColor(entry.authorNickname);
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255,255,255,.8)';
  ctx.stroke();
  ctx.fillStyle = '#fff';
  ctx.font = '800 9px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.direction = 'rtl';
  ctx.fillText(initials(entry.authorNickname), avatarX, y + 20);

  ctx.font = '800 12px Arial, sans-serif';
  ctx.fillStyle = '#f8ddff';
  ctx.textAlign = right ? 'right' : 'left';
  ctx.fillText(entry.authorNickname, nameX, y + 14);
  ctx.font = '500 9px Arial, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,.65)';
  ctx.fillText(entry.kind === 'drawing' ? 'رسم ما فهمه' : 'كتب هذه الجملة', nameX, y + 29);

  const contentY = y + 38;
  const contentH = height - 38;
  ctx.shadowColor = 'rgba(4,8,38,.25)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 3;
  ctx.fillStyle = entry.kind === 'drawing' ? '#ffffff' : right ? '#ffffff' : '#f3efff';
  roundRect(ctx, x, contentY, bubbleW, contentH, 16);
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  if (entry.kind === 'text') {
    ctx.direction = 'rtl';
    ctx.textAlign = 'right';
    ctx.fillStyle = '#202236';
    ctx.font = '700 17px Arial, sans-serif';
    const lines = wrapText(ctx, entry.value || '…', bubbleW - 28).slice(0, 4);
    lines.forEach((line, lineIndex) => ctx.fillText(line, x + bubbleW - 14, contentY + 31 + lineIndex * 23));
  } else {
    const pad = 10;
    await drawDrawingEntry(
      ctx,
      entry,
      x + pad,
      contentY + pad,
      bubbleW - pad * 2,
      contentH - pad * 2,
      Math.max(0, Math.min(1, drawingProgress)),
      canvasFormat,
      imageCache
    );
  }

  if (entry.skipped) {
    ctx.fillStyle = '#8d5c16';
    ctx.font = '600 9px Arial, sans-serif';
    ctx.textAlign = right ? 'right' : 'left';
    ctx.fillText('⏱ انتهى الوقت', right ? x + bubbleW - 10 : x + 10, contentY + contentH - 8);
  }
}

async function drawDrawingEntry(
  ctx: CanvasRenderingContext2D,
  entry: ChainEntry,
  x: number,
  y: number,
  w: number,
  h: number,
  progress: number,
  canvasFormat: CanvasFormat,
  imageCache: Map<string, HTMLImageElement>
): Promise<void> {
  const targetRatio = canvasFormat === 'square' ? 1 : 16 / 10;
  let drawW = w;
  let drawH = drawW / targetRatio;
  if (drawH > h) {
    drawH = h;
    drawW = drawH * targetRatio;
  }
  const dx = x + (w - drawW) / 2;
  const dy = y + (h - drawH) / 2;

  ctx.fillStyle = '#ffffff';
  roundRect(ctx, dx, dy, drawW, drawH, 8);
  ctx.fill();
  ctx.save();
  roundRect(ctx, dx, dy, drawW, drawH, 8);
  ctx.clip();

  // For a completed drawing always use the exact submitted image. This keeps the
  // final GIF frame pixel-faithful to what the player actually submitted and also
  // provides a safe fallback if replay metadata is incomplete.
  if (progress >= 0.999 && entry.value.startsWith('data:image')) {
    try {
      const image = await loadImage(entry.value, imageCache);
      ctx.drawImage(image, dx, dy, drawW, drawH);
    } catch {
      if (entry.replay?.strokes.length) {
        const replayCanvas = document.createElement('canvas');
        replayCanvas.width = Math.max(1, Math.round(drawW));
        replayCanvas.height = Math.max(1, Math.round(drawH));
        const replayCtx = replayCanvas.getContext('2d', { willReadFrequently: true });
        if (replayCtx) {
          replayCtx.fillStyle = '#ffffff';
          replayCtx.fillRect(0, 0, replayCanvas.width, replayCanvas.height);
          drawReplayAtProgress(replayCtx, replayCanvas, entry.replay.strokes, entry.replay.durationMs, 1);
          ctx.drawImage(replayCanvas, dx, dy, drawW, drawH);
        } else {
          drawMissingImage(ctx, dx, dy, drawW, drawH);
        }
      } else {
        drawMissingImage(ctx, dx, dy, drawW, drawH);
      }
    }
  } else if (entry.replay?.strokes.length) {
    const replayCanvas = document.createElement('canvas');
    replayCanvas.width = Math.max(1, Math.round(drawW));
    replayCanvas.height = Math.max(1, Math.round(drawH));
    const replayCtx = replayCanvas.getContext('2d', { willReadFrequently: true });
    if (replayCtx) {
      replayCtx.fillStyle = '#ffffff';
      replayCtx.fillRect(0, 0, replayCanvas.width, replayCanvas.height);
      drawReplayAtProgress(replayCtx, replayCanvas, entry.replay.strokes, entry.replay.durationMs, progress);
      ctx.drawImage(replayCanvas, dx, dy, drawW, drawH);
    } else {
      drawMissingImage(ctx, dx, dy, drawW, drawH);
    }
  } else if (entry.value.startsWith('data:image')) {
    try {
      const image = await loadImage(entry.value, imageCache);
      ctx.drawImage(image, dx, dy, drawW, drawH);
    } catch {
      drawMissingImage(ctx, dx, dy, drawW, drawH);
    }
  } else {
    drawMissingImage(ctx, dx, dy, drawW, drawH);
  }
  ctx.restore();
}

function drawReplayAtProgress(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  strokes: ReplayStroke[],
  durationMs: number,
  progress: number
): void {
  const cutoff = Math.max(0, durationMs) * progress;
  for (const stroke of strokes) {
    if (!stroke.points.length) continue;
    const tool = stroke.tool ?? (stroke.eraser ? 'eraser' : 'pencil');
    if (tool === 'pencil' || tool === 'eraser') {
      const points = stroke.points.filter((point) => point.t <= cutoff);
      if (!points.length) continue;
      drawReplayStroke(ctx, canvas, stroke, points);
    } else {
      const endTime = stroke.points.at(-1)?.t ?? 0;
      if (endTime <= cutoff) drawReplayStroke(ctx, canvas, stroke, stroke.points);
    }
  }
}

function drawReplayStroke(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  stroke: ReplayStroke,
  points: ReplayStroke['points']
): void {
  if (!points.length) return;
  const tool = stroke.tool ?? (stroke.eraser ? 'eraser' : 'pencil');
  const start = points[0];
  const end = points.at(-1) ?? start;
  // stroke.size is stored in CSS pixels (3..40). It must NOT be multiplied by
  // the canvas dimensions. The previous exporter did that, producing line widths
  // in the thousands of pixels and turning drawings into solid black rectangles.
  // Scale gently to the compact GIF drawing card instead.
  const brushScale = Math.max(0.45, Math.min(1.25, canvas.width / 420));

  if (tool === 'fill') {
    floodFill(ctx, canvas, start.x, start.y, stroke.color, stroke.opacity ?? 1);
    return;
  }

  ctx.save();
  ctx.globalAlpha = stroke.opacity ?? 1;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : stroke.color;
  ctx.fillStyle = tool === 'eraser' ? '#ffffff' : stroke.color;
  const maxBrush = Math.max(6, Math.min(canvas.width, canvas.height) * 0.18);
  ctx.lineWidth = Math.max(1, Math.min(maxBrush, stroke.size * brushScale));

  if (tool === 'line') {
    ctx.beginPath();
    ctx.moveTo(start.x * canvas.width, start.y * canvas.height);
    ctx.lineTo(end.x * canvas.width, end.y * canvas.height);
    ctx.stroke();
  } else if (tool === 'rect' || tool === 'rect-fill') {
    const rx = Math.min(start.x, end.x) * canvas.width;
    const ry = Math.min(start.y, end.y) * canvas.height;
    const rw = Math.abs(end.x - start.x) * canvas.width;
    const rh = Math.abs(end.y - start.y) * canvas.height;
    if (tool === 'rect-fill') ctx.fillRect(rx, ry, rw, rh);
    else ctx.strokeRect(rx, ry, rw, rh);
  } else if (tool === 'ellipse' || tool === 'ellipse-fill') {
    const cx = ((start.x + end.x) / 2) * canvas.width;
    const cy = ((start.y + end.y) / 2) * canvas.height;
    const rx = Math.abs(end.x - start.x) * canvas.width / 2;
    const ry = Math.abs(end.y - start.y) * canvas.height / 2;
    ctx.beginPath();
    ctx.ellipse(cx, cy, Math.max(.5, rx), Math.max(.5, ry), 0, 0, Math.PI * 2);
    if (tool === 'ellipse-fill') ctx.fill();
    else ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.moveTo(start.x * canvas.width, start.y * canvas.height);
    if (points.length === 1) {
      ctx.arc(start.x * canvas.width, start.y * canvas.height, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x * canvas.width, points[i].y * canvas.height);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function floodFill(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  nx: number,
  ny: number,
  color: string,
  opacity: number
): void {
  const width = canvas.width;
  const height = canvas.height;
  const x = Math.max(0, Math.min(width - 1, Math.round(nx * (width - 1))));
  const y = Math.max(0, Math.min(height - 1, Math.round(ny * (height - 1))));
  const image = ctx.getImageData(0, 0, width, height);
  const data = image.data;
  const start = (y * width + x) * 4;
  const target = [data[start], data[start + 1], data[start + 2], data[start + 3]];
  const fill = parseColor(color, opacity);
  if (Math.abs(target[0] - fill[0]) < 3 && Math.abs(target[1] - fill[1]) < 3 && Math.abs(target[2] - fill[2]) < 3) return;

  const stack = [y * width + x];
  const visited = new Uint8Array(width * height);
  const match = (i: number) => Math.abs(data[i] - target[0]) < 8 && Math.abs(data[i + 1] - target[1]) < 8 && Math.abs(data[i + 2] - target[2]) < 8 && Math.abs(data[i + 3] - target[3]) < 8;
  let processed = 0;
  while (stack.length && processed < width * height) {
    const pos = stack.pop()!;
    if (visited[pos]) continue;
    visited[pos] = 1;
    const px = pos % width;
    const py = Math.floor(pos / width);
    const di = pos * 4;
    if (!match(di)) continue;
    data[di] = fill[0]; data[di + 1] = fill[1]; data[di + 2] = fill[2]; data[di + 3] = 255;
    processed++;
    if (px > 0) stack.push(pos - 1);
    if (px < width - 1) stack.push(pos + 1);
    if (py > 0) stack.push(pos - width);
    if (py < height - 1) stack.push(pos + width);
  }
  ctx.putImageData(image, 0, 0);
}

function parseColor(hex: string, opacity: number): [number, number, number, number] {
  const normalized = hex.replace('#', '');
  const value = Number.parseInt(normalized.length === 3 ? normalized.split('').map((c) => c + c).join('') : normalized, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255, Math.round(Math.max(0, Math.min(1, opacity)) * 255)];
}

function drawMissingImage(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  ctx.fillStyle = '#f4f3f8';
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = '#77758b';
  ctx.textAlign = 'center';
  ctx.direction = 'rtl';
  ctx.font = '600 12px Arial, sans-serif';
  ctx.fillText('الرسم غير متاح', x + w / 2, y + h / 2);
}

async function loadImage(src: string, cache: Map<string, HTMLImageElement>): Promise<HTMLImageElement> {
  const cached = cache.get(src);
  if (cached) return cached;
  const image = new Image();
  image.decoding = 'async';
  image.src = src;
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('Image load failed'));
  });
  cache.set(src, image);
  return image;
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return ['…'];
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !line) line = candidate;
    else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
  const palette = ['#fa5a9d', '#8e6cff', '#00a8d6', '#17a777', '#e48a2a', '#dd5f52', '#6077e7'];
  return palette[Math.abs(hash) % palette.length];
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, radius: number): void {
  const r = Math.min(radius, Math.abs(w) / 2, Math.abs(h) / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function rgbaToRgb332(rgba: Uint8ClampedArray): Uint8Array {
  const out = new Uint8Array(rgba.length / 4);
  for (let i = 0, j = 0; i < rgba.length; i += 4, j++) {
    const alpha = rgba[i + 3] / 255;
    const r = Math.round(rgba[i] * alpha + 255 * (1 - alpha));
    const g = Math.round(rgba[i + 1] * alpha + 255 * (1 - alpha));
    const b = Math.round(rgba[i + 2] * alpha + 255 * (1 - alpha));
    out[j] = (r & 0xe0) | ((g & 0xe0) >> 3) | (b >> 6);
  }
  return out;
}

function rgb332Palette(): Uint8Array {
  const palette = new Uint8Array(256 * 3);
  for (let i = 0; i < 256; i++) {
    const r = (i >> 5) & 0x07;
    const g = (i >> 2) & 0x07;
    const b = i & 0x03;
    palette[i * 3] = Math.round((r / 7) * 255);
    palette[i * 3 + 1] = Math.round((g / 7) * 255);
    palette[i * 3 + 2] = Math.round((b / 3) * 255);
  }
  return palette;
}

class ByteWriter {
  private readonly chunks: ArrayBuffer[] = [];
  private current = new Uint8Array(64 * 1024);
  private offset = 0;

  private flush(): void {
    if (!this.offset) return;
    const chunk = new Uint8Array(this.offset);
    chunk.set(this.current.subarray(0, this.offset));
    this.chunks.push(chunk.buffer);
    this.current = new Uint8Array(64 * 1024);
    this.offset = 0;
  }

  byte(value: number): void {
    if (this.offset >= this.current.length) this.flush();
    this.current[this.offset++] = value & 0xff;
  }

  word(value: number): void {
    this.byte(value);
    this.byte(value >> 8);
  }

  bytes(values: ArrayLike<number>): void {
    let sourceOffset = 0;
    while (sourceOffset < values.length) {
      if (this.offset >= this.current.length) this.flush();
      const available = this.current.length - this.offset;
      const length = Math.min(available, values.length - sourceOffset);
      for (let i = 0; i < length; i++) this.current[this.offset + i] = values[sourceOffset + i] & 0xff;
      this.offset += length;
      sourceOffset += length;
    }
  }

  ascii(value: string): void {
    for (let i = 0; i < value.length; i++) this.byte(value.charCodeAt(i));
  }

  toBlob(type: string): Blob {
    this.flush();
    return new Blob(this.chunks, { type });
  }
}

class GifEncoder {
  private writer = new ByteWriter();
  private finished = false;

  constructor(private width: number, private height: number, loopCount: number) {
    this.writer.ascii('GIF89a');
    this.writer.word(width);
    this.writer.word(height);
    this.writer.byte(0xf7); // Global 256-color table, 8-bit color resolution.
    this.writer.byte(0);
    this.writer.byte(0);
    this.writer.bytes(rgb332Palette());

    // NETSCAPE loop extension. loopCount=0 means forever.
    this.writer.bytes([0x21, 0xff, 0x0b]);
    this.writer.ascii('NETSCAPE2.0');
    this.writer.bytes([0x03, 0x01]);
    this.writer.word(loopCount);
    this.writer.byte(0x00);
  }

  addFrame(indexedPixels: Uint8Array, delayCs: number): void {
    if (this.finished) throw new Error('GIF already finalized.');
    if (indexedPixels.length !== this.width * this.height) throw new Error('Invalid GIF frame size.');

    // Graphic Control Extension; disposal 1 keeps the frame until the next frame.
    this.writer.bytes([0x21, 0xf9, 0x04, 0x04]);
    this.writer.word(Math.max(2, Math.min(65535, delayCs)));
    this.writer.bytes([0x00, 0x00]);

    // Image Descriptor using the global palette.
    this.writer.byte(0x2c);
    this.writer.word(0);
    this.writer.word(0);
    this.writer.word(this.width);
    this.writer.word(this.height);
    this.writer.byte(0x00);

    const minCodeSize = 8;
    this.writer.byte(minCodeSize);
    const compressed = lzwEncode(indexedPixels, minCodeSize);
    for (let offset = 0; offset < compressed.length; offset += 255) {
      const length = Math.min(255, compressed.length - offset);
      this.writer.byte(length);
      this.writer.bytes(compressed.subarray(offset, offset + length));
    }
    this.writer.byte(0x00);
  }

  finish(): Blob {
    if (!this.finished) {
      this.writer.byte(0x3b);
      this.finished = true;
    }
    return this.writer.toBlob('image/gif');
  }
}

function lzwEncode(pixels: Uint8Array, minCodeSize: number): Uint8Array {
  const clearCode = 1 << minCodeSize;
  const endCode = clearCode + 1;
  const firstCode = endCode + 1;
  const output: number[] = [];
  let currentByte = 0;
  let bitCount = 0;
  let codeSize = minCodeSize + 1;
  let nextCode = firstCode;
  let dictionary = new Map<number, number>();

  const writeCode = (code: number) => {
    currentByte |= code << bitCount;
    bitCount += codeSize;
    while (bitCount >= 8) {
      output.push(currentByte & 0xff);
      currentByte >>>= 8;
      bitCount -= 8;
    }
  };

  const resetDictionary = () => {
    dictionary = new Map<number, number>();
    codeSize = minCodeSize + 1;
    nextCode = firstCode;
  };

  writeCode(clearCode);
  if (!pixels.length) {
    writeCode(endCode);
    if (bitCount > 0) output.push(currentByte & 0xff);
    return Uint8Array.from(output);
  }

  let prefix = pixels[0];
  for (let i = 1; i < pixels.length; i++) {
    const suffix = pixels[i];
    const key = (prefix << 8) | suffix;
    const found = dictionary.get(key);
    if (found !== undefined) {
      prefix = found;
      continue;
    }

    writeCode(prefix);
    if (nextCode < 4096) {
      dictionary.set(key, nextCode++);
      if (nextCode > (1 << codeSize) && codeSize < 12) codeSize++;
    } else {
      writeCode(clearCode);
      resetDictionary();
    }
    prefix = suffix;
  }

  writeCode(prefix);
  writeCode(endCode);
  if (bitCount > 0) output.push(currentByte & 0xff);
  return Uint8Array.from(output);
}

function fileTimestamp(): string {
  const now = new Date();
  const two = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}${two(now.getMonth() + 1)}${two(now.getDate())}-${two(now.getHours())}${two(now.getMinutes())}`;
}
